import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api.jsx';
import ConfirmModal from '../components/ConfirmModal.jsx';
import SuccessModal from '../components/SuccessModal.jsx';
import './Events.css';

const WINDOWS_TO_IANA_TIMEZONE = {
  'Eastern Standard Time': 'America/New_York',
  'Central Standard Time': 'America/Chicago',
  'Mountain Standard Time': 'America/Denver',
  'Pacific Standard Time': 'America/Los_Angeles',
  UTC: 'UTC'
};

const getIanaTimeZone = (value = '') => {
  const normalized = String(value || '').trim();
  if (!normalized) return null;
  return WINDOWS_TO_IANA_TIMEZONE[normalized] || normalized;
};

const getDatePartsInTimeZone = (date, timeZone) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  const parts = formatter.formatToParts(date).reduce((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = Number(part.value);
    return acc;
  }, {});

  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second
  };
};

const getUtcDateForWallClockInTimeZone = (components, timeZone) => {
  let utcMillis = Date.UTC(
    components.year,
    components.month - 1,
    components.day,
    components.hour,
    components.minute,
    components.second || 0
  );

  for (let i = 0; i < 2; i += 1) {
    const zonedParts = getDatePartsInTimeZone(new Date(utcMillis), timeZone);
    const zonedMillis = Date.UTC(
      zonedParts.year,
      zonedParts.month - 1,
      zonedParts.day,
      zonedParts.hour,
      zonedParts.minute,
      zonedParts.second || 0
    );
    const targetMillis = Date.UTC(
      components.year,
      components.month - 1,
      components.day,
      components.hour,
      components.minute,
      components.second || 0
    );
    utcMillis += (targetMillis - zonedMillis);
  }

  return new Date(utcMillis);
};

const parseEventDate = (value, eventTimeZone) => {
  if (!value) return null;
  const ianaTimeZone = getIanaTimeZone(eventTimeZone);

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;

    const wallClock = {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
      hour: value.getUTCHours(),
      minute: value.getUTCMinutes(),
      second: value.getUTCSeconds()
    };

    return ianaTimeZone
      ? getUtcDateForWallClockInTimeZone(wallClock, ianaTimeZone)
      : new Date(
        wallClock.year,
        wallClock.month - 1,
        wallClock.day,
        wallClock.hour,
        wallClock.minute,
        wallClock.second,
        value.getUTCMilliseconds()
      );
  }

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      const wallClock = {
        year: Number(year),
        month: Number(month),
        day: Number(day),
        hour: Number(hour),
        minute: Number(minute),
        second: Number(second || 0)
      };

      return ianaTimeZone
        ? getUtcDateForWallClockInTimeZone(wallClock, ianaTimeZone)
        : new Date(
          wallClock.year,
          wallClock.month - 1,
          wallClock.day,
          wallClock.hour,
          wallClock.minute,
          wallClock.second
        );
    }
  }

  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [totalInvitees, setTotalInvitees] = useState(0);
  const [reminderTemplate, setReminderTemplate] = useState(null);
  const [registrationViewFilter, setRegistrationViewFilter] = useState('all');
  const [registrationStatusFilter, setRegistrationStatusFilter] = useState('all');
  const [registrationSearchTerm, setRegistrationSearchTerm] = useState('');
  const [registrationSendAsFilter, setRegistrationSendAsFilter] = useState('all');
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showSendReminderConfirm, setShowSendReminderConfirm] = useState(false);
  const [showReminderSuccessModal, setShowReminderSuccessModal] = useState(false);
  const [reminderSuccessMessage, setReminderSuccessMessage] = useState('');
  const [customReminderMessage, setCustomReminderMessage] = useState('');
  const [sendingReminders, setSendingReminders] = useState(false);
  const [selectedReminderRecipientKeys, setSelectedReminderRecipientKeys] = useState([]);
  const [reminderSearchTerm, setReminderSearchTerm] = useState('');
  const [reminderStatusFilter, setReminderStatusFilter] = useState('all');
  const [reminderSendAsFilter, setReminderSendAsFilter] = useState('all');
  const [showPostEventEmailModal, setShowPostEventEmailModal] = useState(false);
  const [customPostEventMessage, setCustomPostEventMessage] = useState('');
  const [selectedPostEventRecipientKeys, setSelectedPostEventRecipientKeys] = useState([]);
  const [postEventStatusFilter, setPostEventStatusFilter] = useState('all');
  const [postEventSearchTerm, setPostEventSearchTerm] = useState('');
  const [postEventSendAsFilter, setPostEventSendAsFilter] = useState('all');
  const [sendingPostEvent, setSendingPostEvent] = useState(false);
  const [showPostEventSuccessModal, setShowPostEventSuccessModal] = useState(false);
  const [postEventSuccessMessage, setPostEventSuccessMessage] = useState('');
  const [showSendPostEventConfirm, setShowSendPostEventConfirm] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showTemplateChoiceModal, setShowTemplateChoiceModal] = useState(false);
  const [showEditTemplateChoiceModal, setShowEditTemplateChoiceModal] = useState(false);
  const [createdTemplate, setCreatedTemplate] = useState(null);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    questionText: '',
    questionType: 'text',
    isRequired: false,
    sortOrder: 0,
    options: ''
  });
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    notes: ''
  });
  const [answers, setAnswers] = useState({});
  const [editingRegistration, setEditingRegistration] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedNoteRegistration, setSelectedNoteRegistration] = useState(null);
  const [noteResponseMessage, setNoteResponseMessage] = useState('');
  const [sendingNoteResponse, setSendingNoteResponse] = useState(false);
  const [noteResponseHistory, setNoteResponseHistory] = useState([]);
  const [loadingNoteHistory, setLoadingNoteHistory] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    type: null,
    payload: null,
    title: '',
    message: '',
    confirmText: 'Confirm',
    confirmStyle: 'danger'
  });
  const [feedbackModal, setFeedbackModal] = useState({
    show: false,
    title: '',
    message: ''
  });

  const showFeedbackModal = (title, message) => {
    setFeedbackModal({
      show: true,
      title,
      message
    });
  };

  useEffect(() => {
    loadEventDetails();
  }, [id]);

  const loadEventDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/events/${id}`);
      setEvent(response.data.event);
      setRegistrations(response.data.registrations);
      setPendingInvites(response.data.pendingInvites || []);
      setTotalInvitees(response.data.totalInvitees || 0);
      setReminderTemplate(response.data.reminderTemplate || null);
      loadQuestions();
    } catch (err) {
      setError('Failed to load event details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await api.get(`/events/${id}/questions`);
      setQuestions(response.data);
    } catch (err) {
      console.error('Failed to load questions', err);
    }
  };

  const resetQuestionForm = () => {
    setQuestionForm({
      questionText: '',
      questionType: 'text',
      isRequired: false,
      sortOrder: 0,
      options: ''
    });
    setEditingQuestion(null);
  };

  const handleQuestionChange = (e) => {
    const { name, value, type, checked } = e.target;
    setQuestionForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleQuestionSubmit = async (e) => {
    e.preventDefault();
    try {
      const options = questionForm.options
        ? questionForm.options.split(',').map(opt => opt.trim()).filter(Boolean)
        : [];

      const payload = {
        questionText: questionForm.questionText,
        questionType: questionForm.questionType,
        isRequired: questionForm.isRequired,
        sortOrder: Number(questionForm.sortOrder) || 0,
        options
      };

      if (editingQuestion) {
        await api.put(`/events/${id}/questions/${editingQuestion.QuestionId}`, payload);
      } else {
        await api.post(`/events/${id}/questions`, payload);
      }

      resetQuestionForm();
      loadQuestions();
    } catch (err) {
      showFeedbackModal('Unable to Save Question', 'Failed to save question: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleQuestionEdit = (question) => {
    setEditingQuestion(question);
    setQuestionForm({
      questionText: question.QuestionText,
      questionType: question.QuestionType,
      isRequired: question.IsRequired,
      sortOrder: question.SortOrder || 0,
      options: (question.options || []).map(opt => opt.OptionText).join(', ')
    });
  };

  const handleQuestionDelete = async (questionId) => {
    setConfirmDialog({
      show: true,
      type: 'delete-question',
      payload: questionId,
      title: 'Delete Question',
      message: 'Are you sure you want to delete this question?',
      confirmText: 'Delete',
      confirmStyle: 'danger'
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleAddRegistration = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        answers: questions.map(q => ({
          questionId: q.QuestionId,
          answerText: answers[q.QuestionId] || ''
        }))
      };

      if (editingRegistration) {
        await api.put(`/events/${id}/registrations/${editingRegistration.RegistrationId}`, payload);
      } else {
        await api.post(`/events/${id}/registrations`, payload);
      }

      setShowRegistrationModal(false);
      resetForm();
      loadEventDetails();
    } catch (err) {
      showFeedbackModal('Unable to Save Registration', 'Failed to save registration: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditRegistration = (registration) => {
    setEditingRegistration(registration);
    setFormData({
      email: registration.Email || '',
      firstName: registration.FirstName || '',
      lastName: registration.LastName || '',
      company: registration.Company || '',
      phone: registration.Phone || '',
      notes: registration.Notes || ''
    });

    // Load answers
    const answersMap = {};
    (registration.answers || []).forEach(answer => {
      answersMap[answer.QuestionId] = answer.AnswerText;
    });
    setAnswers(answersMap);
    setShowRegistrationModal(true);
  };

  const handleStatusUpdate = async (registrationId, newStatus) => {
    try {
      await api.put(`/events/${id}/registrations/${registrationId}`, { status: newStatus });
      loadEventDetails();
    } catch (err) {
      showFeedbackModal('Unable to Update Status', 'Failed to update status: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteRegistration = async (registrationId) => {
    setConfirmDialog({
      show: true,
      type: 'delete-registration',
      payload: registrationId,
      title: 'Delete Registration',
      message: 'Are you sure you want to delete this registration?',
      confirmText: 'Delete',
      confirmStyle: 'danger'
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      show: false,
      type: null,
      payload: null,
      title: '',
      message: '',
      confirmText: 'Confirm',
      confirmStyle: 'danger'
    });
  };

  const handleConfirmDialog = async () => {
    try {
      if (confirmDialog.type === 'delete-question') {
        await api.delete(`/events/${id}/questions/${confirmDialog.payload}`);
        loadQuestions();
      } else if (confirmDialog.type === 'delete-registration') {
        await api.delete(`/events/${id}/registrations/${confirmDialog.payload}`);
        loadEventDetails();
      }
    } catch (err) {
      const actionLabel = confirmDialog.type === 'delete-question' ? 'delete question' : 'delete registration';
      showFeedbackModal(`Unable to ${actionLabel}`, `Failed to ${actionLabel}: ` + (err.response?.data?.error || err.message));
    } finally {
      closeConfirmDialog();
    }
  };

  const loadNoteResponseHistory = async (registrationId) => {
    if (!registrationId) {
      setNoteResponseHistory([]);
      return;
    }

    try {
      setLoadingNoteHistory(true);
      const response = await api.get(`/events/${id}/registrations/${registrationId}/note-replies`);
      setNoteResponseHistory(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load note reply history', err);
      setNoteResponseHistory([]);
    } finally {
      setLoadingNoteHistory(false);
    }
  };

  const closeNoteModal = () => {
    setShowNoteModal(false);
    setSelectedNoteRegistration(null);
    setNoteResponseMessage('');
    setNoteResponseHistory([]);
    setLoadingNoteHistory(false);
  };

  const handleOpenNoteModal = (registration) => {
    setSelectedNoteRegistration(registration);
    setNoteResponseMessage('');
    setNoteResponseHistory([]);
    setShowNoteModal(true);
    loadNoteResponseHistory(registration.RegistrationId);
  };

  const handleSendNoteResponse = async () => {
    if (!selectedNoteRegistration?.RegistrationId) return;
    if (!noteResponseMessage.trim()) {
      showFeedbackModal('Response Required', 'Please enter a response message');
      return;
    }

    try {
      setSendingNoteResponse(true);
      const response = await api.post(
        `/events/${id}/registrations/${selectedNoteRegistration.RegistrationId}/note-reply`,
        { message: noteResponseMessage }
      );
      showFeedbackModal('Response Sent', 'Response sent successfully');

      if (response.data?.historyItem) {
        setNoteResponseHistory(prev => [response.data.historyItem, ...prev]);
      } else {
        loadNoteResponseHistory(selectedNoteRegistration.RegistrationId);
      }

      setRegistrations(prev => prev.map(reg => (
        reg.RegistrationId === selectedNoteRegistration.RegistrationId
          ? { ...reg, HasNoteReply: true }
          : reg
      )));

      setSelectedNoteRegistration(prev => (
        prev ? { ...prev, HasNoteReply: true } : prev
      ));

      setNoteResponseMessage('');
    } catch (err) {
      showFeedbackModal('Unable to Send Response', 'Failed to send response: ' + (err.response?.data?.error || err.message));
    } finally {
      setSendingNoteResponse(false);
    }
  };

  const handleSendReminders = async () => {
    try {
      setSendingReminders(true);
      const selectedSenderEmail = reminderSendAsFilter !== 'all' 
        ? senderDisplayNameToEmailMap.get(reminderSendAsFilter)
        : null;

      const selectedRecipients = reminderRecipientOptions
        .filter(recipient => selectedReminderRecipientKeys.includes(recipient.key))
        .map(recipient => ({
          email: recipient.Email,
          contactId: recipient.ContactId || null,
          inviteSenderMailbox: recipient.InviteSenderMailbox || null,
          selectedSender: selectedSenderEmail || null
        }));

      const response = await api.post(`/events/${id}/reminders`, {
        customMessage: customReminderMessage,
        recipients: selectedRecipients,
        senderMailbox: selectedSenderEmail || null
      });

      const sent = response.data?.sentCount || 0;
      const failed = response.data?.failedCount || 0;
      setReminderSuccessMessage(`Reminder send complete. Sent: ${sent}${failed ? `, Failed: ${failed}` : ''}`);
      setShowReminderSuccessModal(true);
      setShowReminderModal(false);
      setShowSendReminderConfirm(false);
      setCustomReminderMessage('');
      setSelectedReminderRecipientKeys([]);
      loadEventDetails();
    } catch (err) {
      const apiError = err.response?.data;
      const firstRecipientError = Array.isArray(apiError?.failedRecipients)
        ? apiError.failedRecipients.find(item => item?.error)?.error
        : '';
      const errorMessage = apiError?.error || firstRecipientError || err.message;
      showFeedbackModal('Unable to Send Reminders', 'Failed to send reminders: ' + errorMessage);
    } finally {
      setSendingReminders(false);
    }
  };

  const handleSendPostEventEmail = async () => {
    try {
      setSendingPostEvent(true);
      const selectedSenderEmail = postEventSendAsFilter !== 'all' 
        ? postEventSenderDisplayNameToEmailMap.get(postEventSendAsFilter)
        : null;

      const selectedRecipients = postEventRecipientOptions
        .filter(recipient => selectedPostEventRecipientKeys.includes(recipient.key))
        .map(recipient => ({
          email: recipient.Email,
          contactId: recipient.ContactId || null,
          inviteSenderMailbox: recipient.InviteSenderMailbox || null,
          selectedSender: selectedSenderEmail || null
        }));

      const response = await api.post(`/events/${id}/post-event-email`, {
        customMessage: customPostEventMessage,
        recipients: selectedRecipients,
        senderMailbox: selectedSenderEmail || null
      });

      const sent = response.data?.sentCount || 0;
      const failed = response.data?.failedCount || 0;
      setPostEventSuccessMessage(`Post-event emails sent. Sent: ${sent}${failed ? `, Failed: ${failed}` : ''}`);
      setShowPostEventSuccessModal(true);
      setShowPostEventEmailModal(false);
      setShowSendPostEventConfirm(false);
      setCustomPostEventMessage('');
      setSelectedPostEventRecipientKeys([]);
      loadEventDetails();
    } catch (err) {
      const apiError = err.response?.data;
      const firstRecipientError = Array.isArray(apiError?.failedRecipients)
        ? apiError.failedRecipients.find(item => item?.error)?.error
        : '';
      const errorMessage = apiError?.error || firstRecipientError || err.message;
      setShowPostEventEmailModal(false);
      setShowSendPostEventConfirm(false);
      showFeedbackModal('Unable to Send Post-Event Emails', 'Failed to send post-event emails: ' + errorMessage);
    } finally {
      setSendingPostEvent(false);
    }
  };

  const handleGenerateTemplate = async () => {
    try {
      setCreatingTemplate(true);
      const response = await api.post(`/events/${id}/generate-template`);
      setCreatedTemplate(response.data);
      setShowTemplateModal(true);
      await loadEventDetails();
    } catch (err) {
      showFeedbackModal('Unable to Generate Template', 'Failed to generate template: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreatingTemplate(false);
    }
  };

  const handleOpenTemplateChoiceModal = () => {
    setShowTemplateChoiceModal(true);
  };

  const handleSelectTemplatePath = () => {
    const eventTitle = encodeURIComponent(event?.Title || 'Event');
    setShowTemplateChoiceModal(false);
    navigate(`/templates?new=1&library=1&fromEvent=1&eventId=${event.EventId}&eventTitle=${eventTitle}`);
  };

  const handleUseDefaultInvitationTemplate = () => {
    setShowTemplateChoiceModal(false);
    handleGenerateTemplate();
  };

  const handleOpenEditTemplateChoiceModal = () => {
    setShowEditTemplateChoiceModal(true);
  };

  const handleEditExistingTemplate = () => {
    const eventTitle = encodeURIComponent(event?.Title || 'Event');
    setShowEditTemplateChoiceModal(false);
    navigate(`/templates?edit=${event.TemplateId}&fromEvent=1&eventId=${event.EventId}&eventTitle=${eventTitle}`);
  };

  const handleSelectDifferentTemplate = () => {
    const eventTitle = encodeURIComponent(event?.Title || 'Event');
    setShowEditTemplateChoiceModal(false);
    navigate(`/templates?new=1&library=1&fromEvent=1&eventId=${event.EventId}&eventTitle=${eventTitle}`);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      company: '',
      phone: '',
      notes: ''
    });
    setAnswers({});
    setEditingRegistration(null);
  };

  const formatDate = (dateString, eventTimeZone) => {
    const date = parseEventDate(dateString, eventTimeZone);
    if (!date) return '—';

    const ianaTimeZone = getIanaTimeZone(eventTimeZone);

    try {
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: ianaTimeZone || undefined,
        timeZoneName: ianaTimeZone ? 'short' : undefined
      }).format(date);
    } catch {
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const formatEventContactName = (value = '') => {
    const parts = String(value || '')
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);

    const names = parts
      .map((item) => {
        const bracketIndex = item.indexOf('<');
        if (bracketIndex > -1) {
          return item.slice(0, bracketIndex).trim();
        }

        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(item)) {
          return '';
        }

        return item;
      })
      .filter(Boolean);

    return names.join(', ');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'registered': return '#0078d4';
      case 'confirmed': return '#005a9e';
      case 'attended': return '#107c10';
      case 'cancelled': return '#d13438';
      case 'declined': return '#a4262c';
      case 'no-show': return '#ff8c00';
      case 'waitlisted': return '#6b69d6';
      case 'pending': return '#8a8886';
      default: return '#666';
    }
  };

  const getSendAsDisplayName = (row = {}) => {
    const explicitName = String(row.InviteSenderDisplayName || '').trim();
    if (explicitName) return explicitName;

    const mailbox = String(row.InviteSenderMailbox || '').trim();
    if (!mailbox || !mailbox.includes('@')) return '';

    const localPart = mailbox.split('@')[0] || '';
    if (!localPart) return '';

    return localPart
      .split(/[._-]+/)
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
      .trim();
  };

  const normalizeEmailKey = (value = '') => {
    const normalized = String(value || '')
      .normalize('NFKC')
      .replace(/[\u0000-\u001F\u007F-\u009F\u200B-\u200F\u202A-\u202E\u2060-\u206F\uFEFF]/g, '')
      .trim();

    if (!normalized) return '';

    const emailMatch = normalized.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    const email = emailMatch ? emailMatch[0] : normalized;

    return email
      .replace(/[<>"'`,;]+/g, '')
      .replace(/\s+/g, '')
      .toLowerCase();
  };

  const respondedEmailKeys = new Set(
    registrations
      .map((registration) => normalizeEmailKey(registration?.Email || ''))
      .filter(Boolean)
  );

  const pendingInviteMap = new Map();
  pendingInvites.forEach((invite) => {
    const emailKey = normalizeEmailKey(invite?.Email || '');
    if (!emailKey || respondedEmailKeys.has(emailKey)) {
      return;
    }

    const existing = pendingInviteMap.get(emailKey);
    const existingTime = existing?.InvitedAt ? new Date(existing.InvitedAt).getTime() : 0;
    const nextTime = invite?.InvitedAt ? new Date(invite.InvitedAt).getTime() : 0;
    if (!existing || nextTime >= existingTime) {
      pendingInviteMap.set(emailKey, invite);
    }
  });

  const dedupedPendingInvites = Array.from(pendingInviteMap.values());

  const mergedTableRows = [
    ...registrations,
    ...dedupedPendingInvites.map((invite) => {
      const canonicalEmail = normalizeEmailKey(invite?.Email || '');
      const fallbackEmail = String(invite?.Email || '').trim();
      return {
        RegistrationId: `pending-${canonicalEmail || fallbackEmail || String(invite.ContactId || '')}`,
        Email: canonicalEmail || fallbackEmail,
        FirstName: invite.FirstName,
        LastName: invite.LastName,
        Company: invite.Company,
        Phone: invite.Phone,
        InviteSenderMailbox: invite.InviteSenderMailbox || '',
        InviteSenderDisplayName: invite.InviteSenderDisplayName || '',
        Status: 'pending',
        RegistrationDate: invite.InvitedAt,
        answers: [],
        IsPending: true
      };
    })
  ].sort((a, b) => new Date(b.RegistrationDate || 0) - new Date(a.RegistrationDate || 0));

  const tableRowsByEmail = new Map();
  mergedTableRows.forEach((row) => {
    const emailKey = normalizeEmailKey(row?.Email || '');
    const key = emailKey || `row:${String(row?.RegistrationId || '')}`;
    const existing = tableRowsByEmail.get(key);
    if (!existing) {
      tableRowsByEmail.set(key, row);
      return;
    }

    // Prefer a responded registration over a pending invite for the same email.
    if (existing.IsPending && !row.IsPending) {
      tableRowsByEmail.set(key, row);
      return;
    }
    if (!existing.IsPending && row.IsPending) {
      return;
    }

    // If both are same type, keep the most recent row.
    const existingTime = existing?.RegistrationDate ? new Date(existing.RegistrationDate).getTime() : 0;
    const nextTime = row?.RegistrationDate ? new Date(row.RegistrationDate).getTime() : 0;
    if (nextTime >= existingTime) {
      tableRowsByEmail.set(key, row);
    }
  });

  const tableRows = Array.from(tableRowsByEmail.values()).sort((a, b) => new Date(b.RegistrationDate || 0) - new Date(a.RegistrationDate || 0));
  const getRegistrationRowRenderKey = (row, index) => {
    const normalizedEmail = normalizeEmailKey(row?.Email || '');
    const source = row?.IsPending ? 'pending' : 'responded';
    return `${source}:${normalizedEmail || String(row?.RegistrationId || '')}:${index}`;
  };

  const registrationSendAsOptions = Array.from(new Set(
    tableRows
      .map(getSendAsDisplayName)
      .map((name) => String(name || '').trim())
      .filter(Boolean)
  )).sort((a, b) => a.localeCompare(b));

  const orderedRegistrationStatuses = ['pending', 'registered', 'confirmed', 'attended', 'waitlisted', 'declined', 'cancelled', 'no-show'];
  const availableRegistrationStatuses = new Set(
    tableRows.map((row) => String(row.Status || (row.IsPending ? 'pending' : '')).trim().toLowerCase()).filter(Boolean)
  );
  const registrationStatusOptions = orderedRegistrationStatuses.filter((status) => availableRegistrationStatuses.has(status));
  const registrationStatusCounts = tableRows.reduce((acc, row) => {
    const normalizedStatus = String(row.Status || (row.IsPending ? 'pending' : '')).trim().toLowerCase();
    if (!normalizedStatus) return acc;
    acc[normalizedStatus] = (acc[normalizedStatus] || 0) + 1;
    return acc;
  }, {});
  const formatStatusLabel = (status = '') => String(status)
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

  useEffect(() => {
    if (registrationStatusFilter === 'all') return;
    if (!registrationStatusOptions.includes(registrationStatusFilter)) {
      setRegistrationStatusFilter('all');
    }
  }, [registrationStatusFilter, registrationStatusOptions]);

  const filteredTableRows = tableRows.filter((row) => {
    if (registrationViewFilter === 'pending') return row.IsPending;
    if (registrationViewFilter === 'responded') return !row.IsPending;
    return true;
  }).filter((row) => {
    if (registrationStatusFilter !== 'all') {
      const normalizedStatus = String(row.Status || (row.IsPending ? 'pending' : '')).trim().toLowerCase();
      if (normalizedStatus !== registrationStatusFilter) {
        return false;
      }
    }

    return true;
  }).filter((row) => {
    if (registrationSendAsFilter !== 'all') {
      const sendAsName = getSendAsDisplayName(row);
      if (sendAsName !== registrationSendAsFilter) {
        return false;
      }
    }

    const term = registrationSearchTerm.trim().toLowerCase();
    if (!term) return true;

    const fullName = `${row.FirstName || ''} ${row.LastName || ''}`.trim();
    const haystack = [
      fullName,
      row.Email || '',
      row.Company || ''
    ].join(' ').toLowerCase();

    return haystack.includes(term);
  });

  const exportToCSV = () => {
    const escapeCsvCell = (value) => {
      const text = String(value ?? '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      return `"${text.replace(/"/g, '""')}"`;
    };

    // Build headers: standard fields + question names
    const headers = ['Email', 'First Name', 'Last Name', 'Company', 'Phone', 'Status', 'Registration Date'];
    const questionHeaders = questions.map((q) => q.QuestionText || '');
    headers.push(...questionHeaders);

    // Build rows with answers
    const rows = filteredTableRows.map((r) => {
      const baseData = [
        r.Email || '',
        r.FirstName || '',
        r.LastName || '',
        r.Company || '',
        r.Phone || '',
        r.Status || '',
        r.RegistrationDate ? new Date(r.RegistrationDate).toLocaleDateString() : ''
      ];

      // Add answer for each question
      const answerData = questions.map((q) => {
        const answer = (r.answers || []).find((a) => a.QuestionId === q.QuestionId);
        return answer ? answer.AnswerText : '';
      });

      return [...baseData, ...answerData];
    });

    const csvContent = [
      headers.map(escapeCsvCell).join(','),
      ...rows.map((row) => row.map(escapeCsvCell).join(','))
    ].join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${event.Title.replace(/[^a-z0-9]/gi, '_')}_registrations.csv`;
    a.click();
  };

  if (loading) return <div className="loading">Loading event details...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!event) return <div className="error">Event not found</div>;

  const pendingRecipientRows = pendingInvites
    .filter(invite => String(invite?.Email || '').trim())
    .map((invite) => ({
      key: `pending-${normalizeEmailKey(invite.Email || '') || String(invite.Email || '').trim().toLowerCase()}`,
      Email: String(invite.Email || '').trim(),
      ContactId: invite.ContactId || null,
      FirstName: invite.FirstName || '',
      LastName: invite.LastName || '',
      Company: invite.Company || '',
      InviteSenderMailbox: invite.InviteSenderMailbox || '',
      InviteSenderDisplayName: invite.InviteSenderDisplayName || '',
      Status: 'pending'
    }));

  const respondedRecipientRows = registrations
    .filter(registration => String(registration?.Email || '').trim())
    .map((registration) => ({
      key: `registration-${normalizeEmailKey(registration.Email || '') || String(registration.Email || '').trim().toLowerCase()}`,
      Email: String(registration.Email || '').trim(),
      ContactId: registration.ContactId || null,
      FirstName: registration.FirstName || '',
      LastName: registration.LastName || '',
      Company: registration.Company || '',
      InviteSenderMailbox: registration.InviteSenderMailbox || '',
      InviteSenderDisplayName: registration.InviteSenderDisplayName || '',
      Status: registration.Status || 'registered'
    }));

  const recipientMap = new Map();
  pendingRecipientRows.forEach((recipient) => {
    recipientMap.set(recipient.key, recipient);
  });
  respondedRecipientRows.forEach((recipient) => {
    if (!recipientMap.has(recipient.key)) {
      recipientMap.set(recipient.key, recipient);
    }
  });

  const reminderRecipientOptions = Array.from(recipientMap.values());

  const reminderRecipientStatuses = Array.from(new Set(reminderRecipientOptions.map((recipient) => recipient.Status || 'pending')));
  
  // Extract Event Contact emails and convert to display names
  const eventContactEmails = (event?.EventContact || '')
    .split(/[;,]/)
    .map((email) => String(email || '').trim())
    .filter((email) => email.includes('@'));
  
  const eventContactDisplayNames = eventContactEmails.map((email) => {
    const localPart = email.split('@')[0] || '';
    if (!localPart) return '';
    return localPart
      .split(/[._-]+/)
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
      .trim();
  }).filter(Boolean);
  
  const reminderRecipientSendAsOptions = Array.from(new Set([
    ...reminderRecipientOptions
      .map(getSendAsDisplayName)
      .map((name) => String(name || '').trim())
      .filter(Boolean),
    ...eventContactDisplayNames
  ])).sort((a, b) => a.localeCompare(b));

  // Build mapping from display name to email for sender resolution
  const senderDisplayNameToEmailMap = new Map();
  
  // Map recipient InviteSenderMailbox display names to emails
  reminderRecipientOptions.forEach((recipient) => {
    if (recipient.InviteSenderMailbox) {
      const displayName = getSendAsDisplayName(recipient);
      if (displayName) {
        senderDisplayNameToEmailMap.set(displayName, recipient.InviteSenderMailbox);
      }
    }
  });
  
  // Map Event Contact display names to emails
  eventContactEmails.forEach((email) => {
    const displayName = eventContactDisplayNames[eventContactEmails.indexOf(email)];
    if (displayName) {
      senderDisplayNameToEmailMap.set(displayName, email);
    }
  });

  const filteredReminderRecipientOptions = reminderRecipientOptions.filter((recipient) => {
    if (reminderSendAsFilter !== 'all') {
      const sendAsName = getSendAsDisplayName(recipient);
      if (sendAsName !== reminderSendAsFilter) {
        return false;
      }
    }

    if (reminderStatusFilter !== 'all' && String(recipient.Status || 'pending') !== reminderStatusFilter) {
      return false;
    }

    const term = reminderSearchTerm.trim().toLowerCase();
    if (!term) return true;

    const fullName = `${recipient.FirstName || ''} ${recipient.LastName || ''}`.trim();
    const haystack = [
      fullName,
      recipient.Email || '',
      recipient.Company || ''
    ].join(' ').toLowerCase();

    return haystack.includes(term);
  });

  const defaultPendingSelectionKeys = pendingRecipientRows
    .map(recipient => recipient.key)
    .filter(key => recipientMap.has(key));

  const openReminderModal = () => {
    setSelectedReminderRecipientKeys(defaultPendingSelectionKeys);
    setReminderSearchTerm('');
    setReminderStatusFilter('all');
    setReminderSendAsFilter('all');
    setShowReminderModal(true);
  };

  const openPostEventEmailModal = () => {
    setSelectedPostEventRecipientKeys([]);
    setPostEventSearchTerm('');
    setPostEventStatusFilter('all');
    setPostEventSendAsFilter('all');
    setCustomPostEventMessage('');
    setShowPostEventEmailModal(true);
  };

  const resetPostEventModal = () => {
    setShowPostEventEmailModal(false);
    setShowSendPostEventConfirm(false);
    setCustomPostEventMessage('');
    setSelectedPostEventRecipientKeys([]);
    setPostEventSearchTerm('');
    setPostEventStatusFilter('all');
    setPostEventSendAsFilter('all');
  };

  const selectedReminderCount = selectedReminderRecipientKeys.length;
  const allReminderRecipientKeys = reminderRecipientOptions.map(recipient => recipient.key);
  const filteredReminderRecipientKeys = filteredReminderRecipientOptions.map(recipient => recipient.key);
  const selectedReminderRecipients = reminderRecipientOptions.filter((recipient) =>
    selectedReminderRecipientKeys.includes(recipient.key)
  );

  const toggleReminderRecipient = (recipientKey) => {
    setSelectedReminderRecipientKeys((prev) => {
      if (prev.includes(recipientKey)) {
        return prev.filter((key) => key !== recipientKey);
      }
      return [...prev, recipientKey];
    });
  };

  const selectPendingReminderRecipients = () => {
    setSelectedReminderRecipientKeys(defaultPendingSelectionKeys);
  };

  const selectAllReminderRecipients = () => {
    setSelectedReminderRecipientKeys(filteredReminderRecipientKeys);
  };

  const clearAllReminderRecipients = () => {
    setSelectedReminderRecipientKeys([]);
  };

  const isEventPast = event && parseEventDate(event.EventDate || event.EventEndDate, event.EventTimeZone) < new Date();

  const postEventRecipientRows = tableRows.filter(row => row.Status && row.Status !== 'pending');
  const postEventRecipientMap = new Map();
  postEventRecipientRows.forEach(recipient => {
    const normalizedEmail = (recipient.Email || '').toLowerCase().trim();
    if (normalizedEmail && !postEventRecipientMap.has(normalizedEmail)) {
      postEventRecipientMap.set(normalizedEmail, {
        key: `post-event-${normalizedEmail}`,
        Email: recipient.Email,
        FirstName: recipient.FirstName,
        LastName: recipient.LastName,
        Company: recipient.Company,
        Status: recipient.Status,
        ContactId: recipient.ContactId,
        InviteSenderMailbox: recipient.InviteSenderMailbox || '',
        InviteSenderDisplayName: recipient.InviteSenderDisplayName || ''
      });
    }
  });
  const postEventRecipientOptions = Array.from(postEventRecipientMap.values());

  const filteredPostEventRecipientOptions = postEventRecipientOptions.filter((recipient) => {
    if (postEventStatusFilter !== 'all' && recipient.Status !== postEventStatusFilter) {
      return false;
    }
    if (postEventSendAsFilter !== 'all') {
      const sendAsName = getSendAsDisplayName(recipient);
      if (sendAsName !== postEventSendAsFilter) {
        return false;
      }
    }
    const term = postEventSearchTerm.trim().toLowerCase();
    if (!term) return true;
    const fullName = `${recipient.FirstName || ''} ${recipient.LastName || ''}`.trim();
    const haystack = [
      fullName,
      recipient.Email || '',
      recipient.Company || ''
    ].join(' ').toLowerCase();
    return haystack.includes(term);
  });

  const postEventRecipientStatuses = Array.from(new Set(
    postEventRecipientOptions
      .map(recipient => recipient.Status)
      .filter(Boolean)
  )).sort();

  const postEventRecipientSendAsOptions = Array.from(new Set([
    ...postEventRecipientOptions
      .map(getSendAsDisplayName)
      .map((name) => String(name || '').trim())
      .filter(Boolean),
    ...eventContactDisplayNames
  ])).sort((a, b) => a.localeCompare(b));

  // Build mapping from display name to email for post-event sender resolution
  const postEventSenderDisplayNameToEmailMap = new Map();
  
  // Map recipient InviteSenderMailbox display names to emails
  postEventRecipientOptions.forEach((recipient) => {
    if (recipient.InviteSenderMailbox) {
      const displayName = getSendAsDisplayName(recipient);
      if (displayName) {
        postEventSenderDisplayNameToEmailMap.set(displayName, recipient.InviteSenderMailbox);
      }
    }
  });
  
  // Map Event Contact display names to emails
  eventContactEmails.forEach((email) => {
    const displayName = eventContactDisplayNames[eventContactEmails.indexOf(email)];
    if (displayName) {
      postEventSenderDisplayNameToEmailMap.set(displayName, email);
    }
  });

  const selectedPostEventCount = selectedPostEventRecipientKeys.length;
  const allPostEventRecipientKeys = postEventRecipientOptions.map(recipient => recipient.key);
  const filteredPostEventRecipientKeys = filteredPostEventRecipientOptions.map(recipient => recipient.key);
  const selectedPostEventRecipients = postEventRecipientOptions.filter((recipient) =>
    selectedPostEventRecipientKeys.includes(recipient.key)
  );

  const togglePostEventRecipient = (recipientKey) => {
    setSelectedPostEventRecipientKeys((prev) => {
      if (prev.includes(recipientKey)) {
        return prev.filter((key) => key !== recipientKey);
      }
      return [...prev, recipientKey];
    });
  };

  const selectAllPostEventRecipients = () => {
    setSelectedPostEventRecipientKeys(filteredPostEventRecipientKeys);
  };

  const clearAllPostEventRecipients = () => {
    setSelectedPostEventRecipientKeys([]);
  };

  const stats = {
    total: registrations.length,
    registered: registrations.filter(r => r.Status === 'registered').length,
    confirmed: registrations.filter(r => r.Status === 'confirmed').length,
    attended: registrations.filter(r => r.Status === 'attended').length,
    cancelled: registrations.filter(r => r.Status === 'cancelled').length,
    noShow: registrations.filter(r => r.Status === 'no-show').length,
    waitlisted: registrations.filter(r => r.Status === 'waitlisted').length,
    declined: registrations.filter(r => r.Status === 'declined').length,
    pending: dedupedPendingInvites.length
  };

  const applyStatusQuickFilter = (status = 'all') => {
    setRegistrationViewFilter('all');
    setRegistrationSendAsFilter('all');
    setRegistrationSearchTerm('');
    setRegistrationStatusFilter(status);
  };

  const isStatusCardActive = (status) => registrationStatusFilter === status && registrationViewFilter === 'all';

  return (
    <div className="container">
      <button onClick={() => navigate('/events')} className="btn btn-link back-button">
        ← Back to Events
      </button>

      <ConfirmModal
        show={confirmDialog.show}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={handleConfirmDialog}
        onCancel={closeConfirmDialog}
        confirmText={confirmDialog.confirmText}
        confirmStyle={confirmDialog.confirmStyle}
      />

      <ConfirmModal
        show={showSendReminderConfirm}
        title="Send Reminder Emails"
        message={`Send reminder emails to ${selectedReminderCount} recipient${selectedReminderCount === 1 ? '' : 's'}?`}
        onConfirm={handleSendReminders}
        onCancel={() => setShowSendReminderConfirm(false)}
        confirmText="Send Reminder"
        confirmStyle="primary"
      />

      <SuccessModal
        show={showReminderSuccessModal}
        title="Reminders Sent"
        message={reminderSuccessMessage}
        onClose={() => setShowReminderSuccessModal(false)}
      />

      <ConfirmModal
        show={showSendPostEventConfirm}
        title="Send Post-Event Emails"
        message={`Send post-event thank you emails to ${selectedPostEventCount} recipient${selectedPostEventCount === 1 ? '' : 's'}?`}
        onConfirm={handleSendPostEventEmail}
        onCancel={() => setShowSendPostEventConfirm(false)}
        confirmText="Send Thank You Emails"
        confirmStyle="primary"
      />

      <SuccessModal
        show={showPostEventSuccessModal}
        title="Post-Event Emails Sent"
        message={postEventSuccessMessage}
        onClose={() => setShowPostEventSuccessModal(false)}
      />

      {feedbackModal.show && (
        <div className="modal-overlay" onClick={() => setFeedbackModal({ show: false, title: '', message: '' })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{feedbackModal.title}</h2>
              <button onClick={() => setFeedbackModal({ show: false, title: '', message: '' })} className="close-btn">✕</button>
            </div>

            <p>{feedbackModal.message}</p>

            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setFeedbackModal({ show: false, title: '', message: '' })}
                className="btn btn-primary"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="event-detail-header">
        <div>
          <h1>{event.Title}</h1>
          {event.Description && <p className="event-description">{event.Description}</p>}
        </div>
      </div>

      <div className="event-info-card">
        <div className="event-meta-grid">
          <div className="event-meta-card">
            <div className="event-meta-title">Date & Time</div>
            <div className="event-meta-value">{formatDate(event.EventDate, event.EventTimeZone)}</div>
            {event.EventEndDate && <div className="event-meta-sub">Ends: {formatDate(event.EventEndDate, event.EventTimeZone)}</div>}
          </div>

          <div className="event-meta-card">
            <div className="event-meta-title">Location & Access</div>
            <div className="event-meta-value">{event.Location || '—'}</div>
            {event.VirtualLink && (
              <div className="event-meta-sub">
                <a href={event.VirtualLink} target="_blank" rel="noopener noreferrer">{event.VirtualLink}</a>
              </div>
            )}
          </div>

          <div className="event-meta-card">
            <div className="event-meta-title">Capacity</div>
            <div className="event-meta-value">{event.MaxAttendees ? `${stats.total} / ${event.MaxAttendees}` : `${stats.total} (Unlimited)`}</div>
          </div>

          <div className="event-meta-card event-meta-card-contact">
            <div className="event-meta-title">Event Contact</div>
            <div className="event-meta-value">{formatEventContactName(event.EventContactDisplay || event.EventContact || '') || '—'}</div>
          </div>
        </div>

        
      </div>

      <div className="event-actions-panel">
        <button
          onClick={() => navigate('/events', { state: { editEventId: event.EventId } })}
          className="btn btn-secondary event-action-btn"
          disabled={isEventPast}
          title={isEventPast ? 'Event is past and locked' : 'Edit event details'}
        >
          ✏️ Edit Event
        </button>
        <button
          onClick={() => setShowQuestionsModal(true)}
          className="btn btn-secondary event-action-btn"
          disabled={isEventPast}
          title={isEventPast ? 'Event is past and locked' : 'Manage event questions'}
        >
          ❓ Manage Questions
        </button>
        <button
          onClick={() => navigate(`/campaigns?new=1&templateId=${event.TemplateId}&eventId=${event.EventId}&eventTitle=${encodeURIComponent(event.Title || 'Event')}`)}
          className="btn btn-secondary event-action-btn"
          disabled={!event.TemplateId || isEventPast}
          title={isEventPast ? 'Event is past and locked' : !event.TemplateId ? 'Create an event template first' : 'Create a campaign with this event template'}
        >
          📣 Invite Attendees
        </button>
        {event.TemplateId && (
          <button
            onClick={handleOpenEditTemplateChoiceModal}
            className="btn btn-secondary event-action-btn"
            disabled={isEventPast}
            title={isEventPast ? 'Event is past and locked' : 'View or change the invite template'}
          >
            📧 Template
          </button>
        )}
        {!event.TemplateId && (
          <button
            onClick={handleOpenTemplateChoiceModal}
            className="btn btn-secondary event-action-btn"
            title={isEventPast ? 'Event is past and locked' : 'Generate invitation template'}
            disabled={creatingTemplate || isEventPast}
          >
            {creatingTemplate ? 'Creating...' : '📧 Create Template'}
          </button>
        )}
        <button
          onClick={() => { resetForm(); setShowRegistrationModal(true); }}
          className="btn btn-secondary event-action-btn"
          disabled={isEventPast}
          title={isEventPast ? 'Event is past and locked' : 'Add a new registration'}
        >
          ➕ Add Registration
        </button>
        {filteredTableRows.length > 0 && (
          <button onClick={exportToCSV} className="btn btn-secondary event-action-btn">
            📥 Export CSV
          </button>
        )}
        <button
          type="button"
          className="btn btn-secondary event-action-btn"
          onClick={openReminderModal}
          disabled={isEventPast || reminderRecipientOptions.length === 0}
          title={isEventPast ? 'Event is past and locked' : reminderRecipientOptions.length === 0 ? 'No recipients available for reminders' : 'Send reminder emails'}
        >
          📣 Send Reminder
        </button>
        <button
          type="button"
          className="btn btn-secondary event-action-btn"
          onClick={openPostEventEmailModal}
          disabled={!isEventPast || postEventRecipientOptions.length === 0}
          title={!isEventPast ? 'Send post-event emails after event concludes' : postEventRecipientOptions.length === 0 ? 'No attendees available for post-event emails' : 'Send thank you emails to attendees'}
        >
          📧 Send Post-Event Email
        </button>
      </div>

      <div className="stats-grid">
       <button type="button" className={`stat-card stat-filter-btn ${registrationStatusFilter === 'all' ? 'active' : ''}`} onClick={() => applyStatusQuickFilter('all')}>
          <div className="stat-value">{totalInvitees}</div>
          <div className="stat-label">Total Invitees</div>
        </button>
        <button type="button" className={`stat-card stat-filter-btn ${isStatusCardActive('registered') ? 'active' : ''}`} onClick={() => applyStatusQuickFilter('registered')}>
          <div className="stat-value" style={{ color: getStatusColor('registered') }}>{stats.registered}</div>
          <div className="stat-label">Registered Attendees</div>
        </button>
        <button type="button" className={`stat-card stat-filter-btn ${isStatusCardActive('declined') ? 'active' : ''}`} onClick={() => applyStatusQuickFilter('declined')}>
          <div className="stat-value" style={{ color: getStatusColor('declined') }}>{stats.declined}</div>
          <div className="stat-label">Declined</div>
        </button>
       <button type="button" className={`stat-card stat-filter-btn ${isStatusCardActive('pending') ? 'active' : ''}`} onClick={() => applyStatusQuickFilter('pending')}>
          <div className="stat-value" style={{ color: getStatusColor('pending') }}>{stats.pending}</div>
          <div className="stat-label">Pending RSVP</div>
        </button>
        <button type="button" className={`stat-card stat-filter-btn ${isStatusCardActive('attended') ? 'active' : ''}`} onClick={() => applyStatusQuickFilter('attended')}>
          <div className="stat-value" style={{ color: getStatusColor('attended') }}>{stats.attended}</div>
          <div className="stat-label">Attended</div>
        </button>
        <button type="button" className={`stat-card stat-filter-btn ${isStatusCardActive('confirmed') ? 'active' : ''}`} onClick={() => applyStatusQuickFilter('confirmed')}>
          <div className="stat-value" style={{ color: getStatusColor('confirmed') }}>{stats.confirmed}</div>
          <div className="stat-label">Confirmed</div>
        </button>
        <button type="button" className={`stat-card stat-filter-btn ${isStatusCardActive('waitlisted') ? 'active' : ''}`} onClick={() => applyStatusQuickFilter('waitlisted')}>
          <div className="stat-value" style={{ color: getStatusColor('waitlisted') }}>{stats.waitlisted}</div>
          <div className="stat-label">Waitlisted</div>
        </button>
        
        <button type="button" className={`stat-card stat-filter-btn ${isStatusCardActive('cancelled') ? 'active' : ''}`} onClick={() => applyStatusQuickFilter('cancelled')}>
          <div className="stat-value" style={{ color: getStatusColor('cancelled') }}>{stats.cancelled}</div>
          <div className="stat-label">Cancelled</div>
        </button>
      </div>

      <div className="registrations-section">
        <div className="section-header">
          <h2>Registrations</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select
              value={registrationViewFilter}
              onChange={(e) => setRegistrationViewFilter(e.target.value)}
              className="status-filter"
            >
              <option value="all">All ({tableRows.length})</option>
              <option value="pending">Pending RSVP ({dedupedPendingInvites.length})</option>
              <option value="responded">Responded ({registrations.length})</option>
            </select>
            <select
              value={registrationStatusFilter}
              onChange={(e) => setRegistrationStatusFilter(e.target.value)}
              className="status-filter"
            >
              <option value="all">All Statuses ({tableRows.length})</option>
              {registrationStatusOptions.map((status) => (
                <option key={`status-${status}`} value={status}>{formatStatusLabel(status)} ({registrationStatusCounts[status] || 0})</option>
              ))}
            </select>
            <select
              value={registrationSendAsFilter}
              onChange={(e) => setRegistrationSendAsFilter(e.target.value)}
              className="status-filter"
            >
              <option value="all">All Send As</option>
              {registrationSendAsOptions.map((name) => (
                <option key={`sendas-${name}`} value={name}>{name}</option>
              ))}
            </select>
            <input
              type="text"
              list="registration-search-suggestions"
              value={registrationSearchTerm}
              onChange={(e) => setRegistrationSearchTerm(e.target.value)}
              placeholder="Search name, email, company"
              className="status-filter"
              style={{ minWidth: '260px' }}
            />
            <datalist id="registration-search-suggestions">
              {tableRows.slice(0, 250).map((row) => {
                const fullName = `${row.FirstName || ''} ${row.LastName || ''}`.trim();
                return (
                  <React.Fragment key={`search-${row.RegistrationId}`}>
                    {fullName && <option value={fullName} />}
                    {row.Email && <option value={row.Email} />}
                    {row.Company && <option value={row.Company} />}
                  </React.Fragment>
                );
              })}
            </datalist>
          </div>
        </div>

        {filteredTableRows.length === 0 ? (
          <div className="empty-state">
            <p>No rows match the selected filter.</p>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setRegistrationViewFilter('all');
                setRegistrationStatusFilter('all');
                setRegistrationSendAsFilter('all');
                setRegistrationSearchTerm('');
              }}
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="registrations-table">
              <colgroup>
                <col className="col-name" />
                <col className="col-email" />
                <col className="col-company" />
                <col className="col-phone" />
                <col className="col-status" />
                <col className="col-registered" />
                {questions.map(q => (
                  <col key={`col-${q.QuestionId}`} className="col-question" />
                ))}
                <col className="col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>Phone</th>
                  <th className="status-column">Status</th>
                  <th>Registered</th>
                  {questions.map(q => (
                    <th key={q.QuestionId}>{q.QuestionText}</th>
                  ))}
                  <th className="actions-column">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTableRows.map((reg, index) => (
                  <tr key={getRegistrationRowRenderKey(reg, index)}>
                    <td>
                      <div className="registration-name-cell">
                        <span>{reg.FirstName && reg.LastName ? `${reg.FirstName} ${reg.LastName}` : reg.FirstName || reg.LastName || '—'}</span>
                        {!reg.IsPending && reg.Notes && (
                          <button
                            type="button"
                            className="note-indicator-btn"
                            title="View attendee note"
                            onClick={() => handleOpenNoteModal(reg)}
                          >
                            <span>📝</span>
                            {reg.HasNoteReply && (
                              <span className="note-replied-check" title="Response sent">✓</span>
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                    <td>{reg.Email}</td>
                    <td>{reg.Company || '—'}</td>
                    <td>{reg.Phone || '—'}</td>
                    <td className="status-cell">
                      {reg.IsPending ? (
                        <span style={{ color: getStatusColor('pending'), fontWeight: 'bold' }}>pending</span>
                      ) : (
                        <select
                          className="status-select"
                          value={reg.Status}
                          onChange={(e) => handleStatusUpdate(reg.RegistrationId, e.target.value)}
                          style={{ color: getStatusColor(reg.Status), fontWeight: 'bold', border: 'none', background: 'transparent', cursor: 'pointer' }}
                        >
                          <option value="registered">Registered</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="attended">Attended</option>
                          <option value="waitlisted">Waitlisted</option>
                          <option value="declined">Declined</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="no-show">No Show</option>
                        </select>
                      )}
                    </td>
                    <td>
                      {reg.RegistrationDate
                        ? `${reg.IsPending ? 'Invited' : 'Registered'} ${new Date(reg.RegistrationDate).toLocaleDateString()}`
                        : '—'}
                    </td>
                    {questions.map(q => {
                      const answer = (reg.answers || []).find(a => a.QuestionId === q.QuestionId);
                      return (
                        <td key={`${reg.RegistrationId}-${q.QuestionId}`}>
                          {answer ? answer.AnswerText : '—'}
                        </td>
                      );
                    })}
                    <td className="actions-cell">
                      {reg.IsPending ? (
                        <span style={{ color: '#666' }}>Awaiting RSVP</span>
                      ) : (
                        <>
                          <button 
                            onClick={() => handleEditRegistration(reg)}
                            className="btn btn-secondary btn-sm"
                            style={{marginRight: '8px'}}
                          >
                            ✏️ Edit
                          </button>
                          <button 
                            onClick={() => handleDeleteRegistration(reg.RegistrationId)}
                            className="btn btn-danger btn-sm"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Registration Modal */}
      {showRegistrationModal && (
        <div className="modal-overlay" onClick={() => setShowRegistrationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRegistration ? 'Edit Registration' : 'Add Registration'}</h2>
              <button onClick={() => setShowRegistrationModal(false)} className="close-btn">✕</button>
            </div>
            
            <form onSubmit={handleAddRegistration} className="event-form">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Company</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>
              
              {/* Custom Questions */}
              {questions.length > 0 && (
                <div className="questions-section">
                  <hr />
                  <h3>Event Questions</h3>
                  {questions.map(question => (
                    <div key={question.QuestionId} className="form-group">
                      <label>{question.QuestionText}{question.IsRequired && ' *'}</label>
                      {question.QuestionType === 'text' && (
                        <input
                          type="text"
                          value={answers[question.QuestionId] || ''}
                          onChange={(e) => handleAnswerChange(question.QuestionId, e.target.value)}
                          required={question.IsRequired}
                        />
                      )}
                      {question.QuestionType === 'textarea' && (
                        <textarea
                          value={answers[question.QuestionId] || ''}
                          onChange={(e) => handleAnswerChange(question.QuestionId, e.target.value)}
                          rows="3"
                          required={question.IsRequired}
                        />
                      )}
                      {question.QuestionType === 'select' && (
                        <select
                          value={answers[question.QuestionId] || ''}
                          onChange={(e) => handleAnswerChange(question.QuestionId, e.target.value)}
                          required={question.IsRequired}
                        >
                          <option value="">Select an option</option>
                          {(question.options || []).map(opt => (
                            <option key={opt.OptionId} value={opt.OptionText}>{opt.OptionText}</option>
                          ))}
                        </select>
                      )}
                      {question.QuestionType === 'radio' && (
                        <div className="radio-group">
                          {(question.options || []).map(opt => (
                            <label key={opt.OptionId} className="radio-label">
                              <input
                                type="radio"
                                name={`question-${question.QuestionId}`}
                                value={opt.OptionText}
                                checked={answers[question.QuestionId] === opt.OptionText}
                                onChange={(e) => handleAnswerChange(question.QuestionId, e.target.value)}
                              />
                              {opt.OptionText}
                            </label>
                          ))}
                        </div>
                      )}
                      {question.QuestionType === 'checkbox' && (
                        <div className="checkbox-group">
                          {(question.options || []).map(opt => (
                            <label key={opt.OptionId} className="checkbox-label">
                              <input
                                type="checkbox"
                                value={opt.OptionText}
                                checked={(answers[question.QuestionId] || '').includes(opt.OptionText)}
                                onChange={(e) => {
                                  const current = answers[question.QuestionId] || '';
                                  const currentArray = current ? current.split('|') : [];
                                  if (e.target.checked) {
                                    handleAnswerChange(question.QuestionId, [...currentArray, opt.OptionText].join('|'));
                                  } else {
                                    handleAnswerChange(question.QuestionId, currentArray.filter(item => item !== opt.OptionText).join('|'));
                                  }
                                }}
                              />
                              {opt.OptionText}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              <div className="modal-actions">
                <button type="button" onClick={() => setShowRegistrationModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingRegistration ? 'Update Registration' : 'Add Registration'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Questions Modal */}
      {showQuestionsModal && (
        <div className="modal-overlay" onClick={() => { setShowQuestionsModal(false); resetQuestionForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Event Questions</h2>
              <button onClick={() => { setShowQuestionsModal(false); resetQuestionForm(); }} className="close-btn">✕</button>
            </div>

            {questions.length === 0 ? (
              <div className="empty-state">
                <p>No custom questions yet.</p>
              </div>
            ) : (
              <div className="questions-list">
                {questions.map(question => (
                  <div key={question.QuestionId} className="question-item">
                    <div>
                      <strong>{question.QuestionText}</strong>
                      <div className="question-meta">
                        Type: {question.QuestionType} {question.IsRequired ? '• Required' : ''}
                      </div>
                      {question.options && question.options.length > 0 && (
                        <div className="question-options">Options: {question.options.map(opt => opt.OptionText).join(', ')}</div>
                      )}
                    </div>
                    <div className="question-actions">
                      <button onClick={() => handleQuestionEdit(question)} className="btn btn-secondary btn-sm">Edit</button>
                      <button onClick={() => handleQuestionDelete(question.QuestionId)} className="btn btn-danger btn-sm">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={handleQuestionSubmit} className="event-form">
              <div className="form-group">
                <label>Question Text *</label>
                <input
                  type="text"
                  name="questionText"
                  value={questionForm.questionText}
                  onChange={handleQuestionChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Question Type *</label>
                  <select name="questionType" value={questionForm.questionType} onChange={handleQuestionChange}>
                    <option value="text">Text</option>
                    <option value="textarea">Long Text</option>
                    <option value="select">Dropdown</option>
                    <option value="radio">Single Choice</option>
                    <option value="checkbox">Multiple Choice</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Sort Order</label>
                  <input
                    type="number"
                    name="sortOrder"
                    value={questionForm.sortOrder}
                    onChange={handleQuestionChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="isRequired"
                    checked={questionForm.isRequired}
                    onChange={handleQuestionChange}
                  />
                  Required
                </label>
              </div>

              {(questionForm.questionType === 'select' || questionForm.questionType === 'radio' || questionForm.questionType === 'checkbox') && (
                <div className="form-group">
                  <label>Options (comma separated)</label>
                  <input
                    type="text"
                    name="options"
                    value={questionForm.options}
                    onChange={handleQuestionChange}
                    placeholder="Small, Medium, Large"
                  />
                </div>
              )}

              <div className="modal-actions">
                {editingQuestion && (
                  <button type="button" onClick={resetQuestionForm} className="btn btn-secondary">
                    Cancel Edit
                  </button>
                )}
                <button type="submit" className="btn btn-primary">
                  {editingQuestion ? 'Update Question' : 'Add Question'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Registration Note Modal */}
      {showNoteModal && selectedNoteRegistration && (
        <div className="modal-overlay" onClick={() => !sendingNoteResponse && closeNoteModal()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Attendee Note</h2>
              <button onClick={() => !sendingNoteResponse && closeNoteModal()} className="close-btn">✕</button>
            </div>

            <div className="event-form">
              <div className="form-group">
                <label>From</label>
                <div className="note-summary">
                  {(selectedNoteRegistration.FirstName || selectedNoteRegistration.LastName)
                    ? `${selectedNoteRegistration.FirstName || ''} ${selectedNoteRegistration.LastName || ''}`.trim()
                    : selectedNoteRegistration.Email}
                  {selectedNoteRegistration.Email ? ` (${selectedNoteRegistration.Email})` : ''}
                </div>
              </div>

              <div className="form-group">
                <label>Attendee Note</label>
                <div className="note-content-box">
                  {selectedNoteRegistration.Notes || 'No note provided.'}
                </div>
              </div>

              <div className="form-group">
                <label>Response History</label>
                {loadingNoteHistory ? (
                  <div className="note-summary">Loading response history...</div>
                ) : noteResponseHistory.length === 0 ? (
                  <div className="note-summary">No responses have been sent yet.</div>
                ) : (
                  <div className="note-history-list">
                    {noteResponseHistory.map((item) => (
                      <div key={item.NoteReplyId} className="note-history-item">
                        <div className="note-history-meta">
                          <strong>
                            {(() => {
                              const senderName = String(item.SentByName || '').trim() || 'Team';
                              const senderMailbox = String(item.SenderMailbox || '').trim();
                              if (!senderMailbox) return senderName;

                              const normalizedName = senderName.toLowerCase();
                              const normalizedMailbox = senderMailbox.toLowerCase();
                              return normalizedName === normalizedMailbox
                                ? senderName
                                : `${senderName} <${senderMailbox}>`;
                            })()}
                          </strong>
                          {' · '}
                          {new Date(item.CreatedAt).toLocaleString()}
                        </div>
                        <div className="note-history-message">{item.Message}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Response Email</label>
                <textarea
                  rows="5"
                  value={noteResponseMessage}
                  onChange={(e) => setNoteResponseMessage(e.target.value)}
                  placeholder="Type your response to this attendee..."
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={closeNoteModal}
                  className="btn btn-secondary"
                  disabled={sendingNoteResponse}
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleSendNoteResponse}
                  className="btn btn-primary"
                  disabled={sendingNoteResponse || !noteResponseMessage.trim()}
                >
                  {sendingNoteResponse ? 'Sending...' : 'Send Response'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Success Modal */}
      {showTemplateModal && (
        <div className="modal-overlay" onClick={() => setShowTemplateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Template Created</h2>
              <button onClick={() => setShowTemplateModal(false)} className="close-btn">✕</button>
            </div>

            <p>
              Template <strong>{createdTemplate?.Name}</strong> was created successfully.
            </p>

            {createdTemplate?.TemplateId && (
              <a
                className="template-link"
                href={`/templates?edit=${createdTemplate.TemplateId}`}
              >
                View and edit this template
              </a>
            )}

            <div className="modal-actions">
              <button type="button" onClick={() => setShowTemplateModal(false)} className="btn btn-primary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Choice Modal */}
      {showEditTemplateChoiceModal && (
        <div className="modal-overlay" onClick={() => setShowEditTemplateChoiceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Event Invitation Template</h2>
              <button onClick={() => setShowEditTemplateChoiceModal(false)} className="close-btn">✕</button>
            </div>

            <p style={{ marginBottom: '16px' }}>
              This event already has a linked invitation template. What would you like to do?
            </p>

            <div className="modal-actions" style={{ justifyContent: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
              <button
                type="button"
                onClick={handleEditExistingTemplate}
                className="btn btn-primary"
              >
                ✏️ Edit Existing Template
              </button>
              <button
                type="button"
                onClick={handleSelectDifferentTemplate}
                className="btn btn-secondary"
              >
                📚 Select a Different Template
              </button>
              <button
                type="button"
                onClick={() => setShowEditTemplateChoiceModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Template Choice Modal */}
      {showTemplateChoiceModal && (
        <div className="modal-overlay" onClick={() => setShowTemplateChoiceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Invitation Template</h2>
              <button onClick={() => setShowTemplateChoiceModal(false)} className="close-btn">✕</button>
            </div>

            <p style={{ marginBottom: '16px' }}>
              Choose how you want to create the invitation template for this event.
            </p>

            <div className="modal-actions" style={{ justifyContent: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
              <button
                type="button"
                onClick={handleSelectTemplatePath}
                className="btn btn-secondary"
              >
                📚 Select from Template Library
              </button>
              <button
                type="button"
                onClick={handleUseDefaultInvitationTemplate}
                className="btn btn-primary"
                disabled={creatingTemplate}
              >
                {creatingTemplate ? 'Creating...' : '⚡ Use Default Invitation Template'}
              </button>
              <button
                type="button"
                onClick={() => setShowTemplateChoiceModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reminder Modal */}
      {showPostEventEmailModal && (
        <div className="modal-overlay" onClick={() => !sendingPostEvent && setShowPostEventEmailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Send Post-Event Thank You Email</h2>
              <button onClick={() => !sendingPostEvent && setShowPostEventEmailModal(false)} className="close-btn">✕</button>
            </div>

            <div className="form-group">
              <label>Recipients ({selectedPostEventCount} selected)</label>
              {postEventRecipientOptions.length === 0 ? (
                <p>No recipients found. Event must have attendees or responded registrations.</p>
              ) : (
                <>
                  {(postEventSearchTerm.trim() || postEventStatusFilter !== 'all' || postEventSendAsFilter !== 'all') && (
                    <div style={{ marginBottom: '8px', fontSize: '12px', color: '#555' }}>
                      Active filters: {postEventSearchTerm.trim() ? `Search "${postEventSearchTerm.trim()}"` : 'Search: none'} · {postEventStatusFilter !== 'all' ? `Status: ${postEventStatusFilter}` : 'Status: all'} · {postEventSendAsFilter !== 'all' ? `Send As: ${postEventSendAsFilter}` : 'Send As: all'}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      list="post-event-recipient-search-suggestions"
                      value={postEventSearchTerm}
                      onChange={(e) => setPostEventSearchTerm(e.target.value)}
                      placeholder="Search name, email, company"
                      style={{ flex: '1 1 260px', minWidth: '220px' }}
                    />
                    <select
                      value={postEventStatusFilter}
                      onChange={(e) => setPostEventStatusFilter(e.target.value)}
                      style={{ minWidth: '170px' }}
                    >
                      <option value="all">All statuses</option>
                      {postEventRecipientStatuses.map((status) => (
                        <option key={`post-event-status-${status}`} value={status}>{status}</option>
                      ))}
                    </select>
                    <select
                      value={postEventSendAsFilter}
                      onChange={(e) => setPostEventSendAsFilter(e.target.value)}
                      style={{ minWidth: '190px' }}
                    >
                      <option value="all">All Send As</option>
                      {postEventRecipientSendAsOptions.map((name) => (
                        <option key={`post-event-sendas-${name}`} value={name}>{name}</option>
                      ))}
                    </select>
                    <datalist id="post-event-recipient-search-suggestions">
                      {postEventRecipientOptions.slice(0, 300).map((recipient) => {
                        const fullName = `${recipient.FirstName || ''} ${recipient.LastName || ''}`.trim();
                        return (
                          <React.Fragment key={`post-event-search-${recipient.key}`}>
                            {fullName && <option value={fullName} />}
                            {recipient.Email && <option value={recipient.Email} />}
                            {recipient.Company && <option value={recipient.Company} />}
                          </React.Fragment>
                        );
                      })}
                    </datalist>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={selectAllPostEventRecipients}
                      disabled={sendingPostEvent || filteredPostEventRecipientOptions.length === 0}
                    >
                      Select visible
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={clearAllPostEventRecipients}
                      disabled={sendingPostEvent}
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="reminder-recipient-list" style={{ maxHeight: '140px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', background: '#fff' }}>
                    <div className="reminder-recipient-items">
                      {filteredPostEventRecipientOptions.map((recipient) => {
                        const displayName = (recipient.FirstName || recipient.LastName)
                          ? `${recipient.FirstName || ''} ${recipient.LastName || ''}`.trim()
                          : recipient.Email;

                        return (
                          <label key={recipient.key} className="reminder-recipient-row">
                            <input
                              type="checkbox"
                              checked={selectedPostEventRecipientKeys.includes(recipient.key)}
                              onChange={() => togglePostEventRecipient(recipient.key)}
                              disabled={sendingPostEvent}
                            />
                            <span className="reminder-recipient-text">
                              <span>{displayName} ({recipient.Email}){recipient.Company ? ` • ${recipient.Company}` : ''}</span>
                              <span style={{ color: getStatusColor(recipient.Status), fontWeight: 600 }}>
                                {recipient.Status}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                      {filteredPostEventRecipientOptions.length === 0 && (
                        <div style={{ padding: '10px', color: '#666' }}>No recipients match current search/filter.</div>
                      )}
                    </div>
                  </div>

                  <div style={{ marginTop: '12px', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fafafa' }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                      Recipients ({selectedPostEventRecipients.length})
                    </div>
                    {selectedPostEventRecipients.length === 0 ? (
                      <div style={{ fontSize: '13px', color: '#666' }}>No recipients selected yet.</div>
                    ) : (
                      <div style={{ maxHeight: '140px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px', padding: '8px', backgroundColor: '#fff' }}>
                        {selectedPostEventRecipients.map((recipient) => {
                          const fullName = `${recipient.FirstName || ''} ${recipient.LastName || ''}`.trim();
                          return (
                            <div key={`selected-post-event-${recipient.key}`} style={{ fontSize: '13px', padding: '2px 0' }}>
                              {fullName ? `${fullName} (${recipient.Email})` : recipient.Email}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="form-group">
              <label>Thank you message (required)</label>
              <textarea
                rows="4"
                value={customPostEventMessage}
                onChange={(e) => setCustomPostEventMessage(e.target.value)}
                placeholder="Enter your thank you message to send to attendees..."
              />
            </div>

            <div className="modal-actions">
              <button
                type="button"
                onClick={resetPostEventModal}
                className="btn btn-secondary"
                disabled={sendingPostEvent}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setShowSendPostEventConfirm(true)}
                className="btn btn-primary"
                disabled={sendingPostEvent || selectedPostEventCount === 0 || !customPostEventMessage.trim()}
              >
                {sendingPostEvent ? 'Sending...' : 'Send Thank You Emails'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReminderModal && (
        <div className="modal-overlay" onClick={() => !sendingReminders && setShowReminderModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Send RSVP Reminder</h2>
              <button onClick={() => !sendingReminders && setShowReminderModal(false)} className="close-btn">✕</button>
            </div>

            <div className="form-group">
              <label>Recipients ({selectedReminderCount} selected)</label>
              {reminderRecipientOptions.length === 0 ? (
                <p>No recipients found.</p>
              ) : (
                <>
                  {(reminderSearchTerm.trim() || reminderStatusFilter !== 'all' || reminderSendAsFilter !== 'all') && (
                    <div style={{ marginBottom: '8px', fontSize: '12px', color: '#555' }}>
                      Active filters: {reminderSearchTerm.trim() ? `Search "${reminderSearchTerm.trim()}"` : 'Search: none'} · {reminderStatusFilter !== 'all' ? `Status: ${reminderStatusFilter}` : 'Status: all'} · {reminderSendAsFilter !== 'all' ? `Send As: ${reminderSendAsFilter}` : 'Send As: all'}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      list="reminder-recipient-search-suggestions"
                      value={reminderSearchTerm}
                      onChange={(e) => setReminderSearchTerm(e.target.value)}
                      placeholder="Search name, email, company"
                      style={{ flex: '1 1 260px', minWidth: '220px' }}
                    />
                    <select
                      value={reminderStatusFilter}
                      onChange={(e) => setReminderStatusFilter(e.target.value)}
                      style={{ minWidth: '170px' }}
                    >
                      <option value="all">All statuses</option>
                      {reminderRecipientStatuses.map((status) => (
                        <option key={`status-${status}`} value={status}>{status}</option>
                      ))}
                    </select>
                    <select
                      value={reminderSendAsFilter}
                      onChange={(e) => setReminderSendAsFilter(e.target.value)}
                      style={{ minWidth: '190px' }}
                    >
                      <option value="all">All Send As</option>
                      {reminderRecipientSendAsOptions.map((name) => (
                        <option key={`reminder-sendas-${name}`} value={name}>{name}</option>
                      ))}
                    </select>
                    <datalist id="reminder-recipient-search-suggestions">
                      {reminderRecipientOptions.slice(0, 300).map((recipient) => {
                        const fullName = `${recipient.FirstName || ''} ${recipient.LastName || ''}`.trim();
                        return (
                          <React.Fragment key={`reminder-search-${recipient.key}`}>
                            {fullName && <option value={fullName} />}
                            {recipient.Email && <option value={recipient.Email} />}
                            {recipient.Company && <option value={recipient.Company} />}
                          </React.Fragment>
                        );
                      })}
                    </datalist>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={selectPendingReminderRecipients}
                      disabled={sendingReminders}
                    >
                      Select pending
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={selectAllReminderRecipients}
                      disabled={sendingReminders || filteredReminderRecipientOptions.length === 0}
                    >
                      Select visible
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={clearAllReminderRecipients}
                      disabled={sendingReminders}
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="reminder-recipient-list" style={{ maxHeight: '140px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', background: '#fff' }}>
                  <div className="reminder-recipient-items">
                    {filteredReminderRecipientOptions.map((recipient) => {
                      const displayName = (recipient.FirstName || recipient.LastName)
                        ? `${recipient.FirstName || ''} ${recipient.LastName || ''}`.trim()
                        : recipient.Email;

                      return (
                        <label key={recipient.key} className="reminder-recipient-row">
                          <input
                            type="checkbox"
                            checked={selectedReminderRecipientKeys.includes(recipient.key)}
                            onChange={() => toggleReminderRecipient(recipient.key)}
                            disabled={sendingReminders}
                          />
                          <span className="reminder-recipient-text">
                            <span>{displayName} ({recipient.Email}){recipient.Company ? ` • ${recipient.Company}` : ''}</span>
                            <span style={{ color: getStatusColor(recipient.Status), fontWeight: 600 }}>
                              {recipient.Status}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                    {filteredReminderRecipientOptions.length === 0 && (
                      <div style={{ padding: '10px', color: '#666' }}>No recipients match current search/filter.</div>
                    )}
                  </div>
                  </div>

                  <div style={{ marginTop: '12px', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fafafa' }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                      Recipients ({selectedReminderRecipients.length})
                    </div>
                    {selectedReminderRecipients.length === 0 ? (
                      <div style={{ fontSize: '13px', color: '#666' }}>No recipients selected yet.</div>
                    ) : (
                      <div style={{ maxHeight: '140px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px', padding: '8px', backgroundColor: '#fff' }}>
                        {selectedReminderRecipients.map((recipient) => {
                          const fullName = `${recipient.FirstName || ''} ${recipient.LastName || ''}`.trim();
                          return (
                            <div key={`selected-${recipient.key}`} style={{ fontSize: '13px', padding: '2px 0' }}>
                              {fullName ? `${fullName} (${recipient.Email})` : recipient.Email}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="form-group">
              <label>Custom reminder message (optional)</label>
              <textarea
                rows="4"
                value={customReminderMessage}
                onChange={(e) => setCustomReminderMessage(e.target.value)}
                placeholder="Add a reminder note to appear above the original invite content..."
              />
            </div>

            <div className="form-group">
              <label>Original invite content</label>
              {reminderTemplate ? (
                <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '12px', maxHeight: '260px', overflowY: 'auto', background: '#fff' }}>
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Subject:</strong> {reminderTemplate.Subject}
                  </div>
                  <div dangerouslySetInnerHTML={{ __html: reminderTemplate.Body }} />
                </div>
              ) : (
                <p>No template is linked to this event.</p>
              )}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                onClick={() => {
                  setShowReminderModal(false);
                  setShowSendReminderConfirm(false);
                }}
                className="btn btn-secondary"
                disabled={sendingReminders}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setShowSendReminderConfirm(true)}
                className="btn btn-primary"
                disabled={sendingReminders || selectedReminderCount === 0 || !reminderTemplate}
              >
                {sendingReminders ? 'Sending...' : 'Send Reminder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetail;
